import React from "react";
import PropTypes from "prop-types";
import { getCanonicalSlug } from "../utils/slug";
import { getContentRating } from "../utils/showFields";
import { buildAggregateRatingSchema } from "../utils/showRatings";

const StructuredData = ({ releases = [] }) => {
    const BASE_URL = 'https://premierelens.com';

    const deriveShowSlug = (series) => getCanonicalSlug(series) || (series.id ? String(series.id) : 'show');

    const showUrl = (series) => `${BASE_URL}/show/${deriveShowSlug(series)}`;

    const generateTVSeriesSchemas = () => {
        return releases.slice(0, 10).map(series => {
            const aggregateRating = buildAggregateRatingSchema(series);

            return {
                "@context": "https://schema.org",
                "@type": "TVSeries",
                "name": series.title,
                ...(Array.isArray(series.alternateTitles) && series.alternateTitles.length > 0
                  ? { "alternateName": series.alternateTitles.slice(0, 5) }
                  : typeof series.originalTitle === "string" && series.originalTitle.trim()
                    ? { "alternateName": series.originalTitle.trim() }
                    : {}),
                "description": series.plotOverview || `${series.title} - scripted television series`,
                "genre": series.genres || [],
                "aggregateRating": aggregateRating || undefined,
                "numberOfSeasons": series.seasonNumber,
                "numberOfEpisodes": series.episodeCount || undefined,
                "startDate": series.year ? `${series.year}-01-01` : undefined,
                "endDate": series.endYear && series.endYear !== series.year ? `${series.endYear}-12-31` : undefined,
                "contentRating": getContentRating(series) || undefined,
                "datePublished": series.releaseDate,
                "url": showUrl(series),
                "image": series.poster,
                "mainEntityOfPage": {
                    "@type": "WebPage",
                    "@id": showUrl(series)
                }
            };
        });
    };

    const generateBreadcrumbSchema = () => ({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": BASE_URL
            }
        ]
    });

    const generateWebSiteSchema = () => ({
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "PremiereLens",
        "alternateName": ["Premiere Lens TV", "Premiere Lens Television"],
        "url": BASE_URL
    });

    const allSchemas = [
        generateWebSiteSchema(),
        generateBreadcrumbSchema(),
        ...generateTVSeriesSchemas()
    ];

    return (
        <>
            {allSchemas.map((schema, index) => (
                <script
                    key={index}
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(schema)
                    }}
                />
            ))}
        </>
    );
};

StructuredData.propTypes = {
    releases: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title: PropTypes.string,
        rating: PropTypes.number,
        omdbData: PropTypes.shape({
            ratingCount: PropTypes.number,
        }),
        seasonNumber: PropTypes.number,
        releaseDate: PropTypes.string,
        year: PropTypes.number,
        endYear: PropTypes.number,
        genres: PropTypes.arrayOf(PropTypes.string),
        networks: PropTypes.arrayOf(PropTypes.string),
        plotOverview: PropTypes.string,
        detailsUrl: PropTypes.string,
        poster: PropTypes.string,
        sourceName: PropTypes.string
    }))
};

export default StructuredData;
